import { Html } from '@react-three/drei';
import type { ConnectionState, SeverityKind } from '../types';

interface UIOverlayProps {
  mode: 'screen' | 'vr';
  services: string[];
  activeService: string | null;
  orgSlug: string | null;
  totalLogs: number;
  fps: number;
  paused: boolean;
  severityFilter: Record<SeverityKind, boolean>;
  timelinePosition: number;
  connectionState: ConnectionState;
  wsUrl: string;
  onTogglePause: () => void;
  onToggleSeverity: (severity: SeverityKind) => void;
  onServiceSelect: (service: string | null) => void;
  onTimelineChange: (value: number) => void;
}

const severityKeys: SeverityKind[] = ['info', 'warn', 'error'];

const connectionStateLabel = (connectionState: ConnectionState): string => {
  if (connectionState === 'connected') return 'LIVE';
  if (connectionState === 'connecting') return 'CONNECTING';
  if (connectionState === 'fallback') return 'FAKE FEED';
  if (connectionState === 'auth_required') return 'AUTH REQUIRED';
  return 'DEGRADED';
};

function PanelContent({
  services,
  activeService,
  orgSlug,
  totalLogs,
  fps,
  paused,
  severityFilter,
  timelinePosition,
  connectionState,
  wsUrl,
  onTogglePause,
  onToggleSeverity,
  onServiceSelect,
  onTimelineChange
}: Omit<UIOverlayProps, 'mode'>) {
  const connectionLabel = connectionStateLabel(connectionState);

  return (
    <>
      <div className="rounded-2xl border border-cyan-300/20 bg-slate-950/75 p-3 shadow-holo backdrop-blur-md">
        <div className="mb-2 flex items-center justify-between gap-4">
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.14em] text-cyan-100">Logverse</h2>
          <span className="rounded-full border border-cyan-200/30 px-2 py-1 font-mono text-[10px] text-cyan-100">
            {connectionLabel}
          </span>
        </div>

        {orgSlug ? (
          <p className="mb-2 rounded-lg border border-cyan-300/25 bg-cyan-300/10 px-2 py-1 font-mono text-[10px] text-cyan-100">
            org scope: {orgSlug}
          </p>
        ) : null}

        <div className="mb-2 grid grid-cols-3 gap-2 text-[11px]">
          <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-1">
            <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Logs</p>
            <p className="font-mono text-cyan-100">{totalLogs}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-1">
            <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">FPS</p>
            <p className="font-mono text-cyan-100">{fps.toFixed(0)}</p>
          </div>
          <div className="rounded-lg border border-white/10 bg-white/5 px-2 py-1">
            <p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">Feed</p>
            <p className="truncate font-mono text-[10px] text-cyan-100">{wsUrl}</p>
          </div>
        </div>

        <div className="mb-2 flex flex-wrap gap-2">
          {severityKeys.map((severity) => (
            <button
              key={severity}
              type="button"
              onClick={() => onToggleSeverity(severity)}
              className={`rounded-full border px-2 py-1 font-mono text-[10px] uppercase tracking-wider transition ${
                severityFilter[severity]
                  ? 'border-cyan-300/60 bg-cyan-300/20 text-cyan-100'
                  : 'border-white/15 bg-white/5 text-slate-400'
              }`}
            >
              {severity}
            </button>
          ))}

          <button
            type="button"
            onClick={onTogglePause}
            className="rounded-full border border-white/20 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-white transition hover:border-cyan-200/60 hover:text-cyan-100"
          >
            {paused ? 'Resume' : 'Pause'}
          </button>
        </div>

        <div className="max-h-32 space-y-1 overflow-y-auto pr-1">
          <button
            type="button"
            onClick={() => onServiceSelect(null)}
            className={`w-full rounded-md border px-2 py-1 text-left font-mono text-[10px] uppercase tracking-wider ${
              activeService === null
                ? 'border-cyan-300/50 bg-cyan-300/15 text-cyan-50'
                : 'border-white/10 bg-white/5 text-slate-300'
            }`}
          >
            all services
          </button>
          {services.map((service) => (
            <button
              key={service}
              type="button"
              onClick={() => onServiceSelect(service)}
              className={`w-full rounded-md border px-2 py-1 text-left font-mono text-[10px] ${
                activeService === service
                  ? 'border-cyan-300/50 bg-cyan-300/15 text-cyan-50'
                  : 'border-white/10 bg-white/5 text-slate-300'
              }`}
            >
              {service}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-cyan-300/20 bg-slate-950/75 p-3 shadow-holo backdrop-blur-md">
        <p className="mb-1 font-mono text-[10px] uppercase tracking-[0.16em] text-cyan-100">Timeline Scrubber</p>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={timelinePosition}
          onChange={(event) => onTimelineChange(Number(event.target.value))}
          className="w-full accent-cyan-300"
        />
      </div>
    </>
  );
}

export default function UIOverlay({ mode, ...props }: UIOverlayProps) {
  if (mode === 'vr') {
    return (
      <group>
        <Html transform occlude position={[-0.9, 1.75, -1.4]} distanceFactor={1.2}>
          <div className="w-80 space-y-2 text-white">
            <PanelContent {...props} />
          </div>
        </Html>
      </group>
    );
  }

  return (
    <div className="pointer-events-none absolute inset-0 z-20 flex flex-col justify-between p-4">
      <div className="pointer-events-auto w-full max-w-sm space-y-2">
        <PanelContent {...props} />
      </div>
    </div>
  );
}
